/**
 * Level 2：在线报告页（独立路由 /report）
 *
 * 设计目标：
 *  1. 链接可分享：URL 自带 score（编码后）+ intent；进入后能完整复现报告
 *  2. 微信浏览器友好：一切排版用纯 HTML/CSS，不依赖 Canvas/PDF runtime
 *  3. 是 Level 3 PDF 的"打印源"：用 @media print 单独优化分页
 *
 * URL 参数：
 *   /report?score=558&intent=邹城一中&year=2025&dm=whittaker
 *
 * 三种入口：
 *   1. 用户测算后点"生成完整报告"按钮 → 把当前状态序列化进 URL → 跳过来
 *   2. 用户分享链接给亲友 → 亲友点开后直接看到完整结果
 *   3. PDF 导出从此页面"打印"
 */
import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { calculate } from '@/domain/probability'
import { POLICIES, PREFERENCE, NARRATIVE } from '@/store/useApp'
import { PROFILES, PROFILES_BY_NAME } from '@/domain/schools'
import { DensityCurve } from '@/components/DensityCurve'
import { SchoolProfileCard } from '@/components/SchoolProfileCard'
import { BackfillPledge } from '@/components/BackfillPledge'
import { exportReportToPdf } from '@/lib/pdfExport'
import { formatProbPercent, probStrength, probToneClass, showProbPercent } from '@/lib/format'
import { detectStage } from '@/domain/stage'
import type { CalcResult, SchoolName } from '@/domain/types'

export function ReportPage() {
  const [params] = useSearchParams()
  const reportRef = useRef<HTMLDivElement>(null)

  const score = parseInt(params.get('score') ?? '0', 10)
  const yearParam = parseInt(params.get('year') ?? '2025', 10)
  const intent = (params.get('intent') as SchoolName | null) ?? null

  const result = useMemo<CalcResult | null>(() => {
    const policy = POLICIES[yearParam] ?? POLICIES[2025]
    if (!score || !policy || policy.qualification_line === null) return null
    return calculate({
      score,
      policy,
      preference: PREFERENCE,
      narrative: NARRATIVE,
      intentSchool: intent,
      densityMethod: 'whittaker'
    })
  }, [score, yearParam, intent])

  useEffect(() => {
    document.title = score ? `${score} 分志愿测算报告 · 方法星球` : '志愿测算报告 · 方法星球'
  }, [score])

  if (!result) {
    return (
      <div className="container-mobile py-20 text-center">
        <h1 className="text-h1 text-ink-900 mb-4">报告参数不完整</h1>
        <p className="text-ink-600 mb-8">请先在主页输入分数后，再点击"生成完整报告"。</p>
        <Link to="/" className="btn-primary inline-block px-6 py-3">
          返回主页
        </Link>
      </div>
    )
  }

  const policy = POLICIES[yearParam] ?? POLICIES[2025]
  const stageInfo = useMemo(() => detectStage(new Date(), policy.qualification_line !== null), [policy])
  const handleExportPdf = async () => {
    if (reportRef.current) {
      await exportReportToPdf(reportRef.current, `邹城中考志愿测算报告_${score}分.pdf`)
    }
  }

  return (
    <div className="bg-ink-50 min-h-screen pb-20">
      {/* 顶部工具栏：仅屏幕显示，打印时隐藏 */}
      <div className="print:hidden sticky top-0 z-20 bg-white border-b border-ink-100 shadow-sm">
        <div className="container-mobile py-3 flex items-center gap-2 justify-between">
          <Link to="/" className="text-ink-600 text-sm active:opacity-70">
            ← 返回测算
          </Link>
          <div className="flex gap-2">
            <button
              onClick={handleExportPdf}
              className="btn-primary px-4 py-2 text-sm"
            >
              下载 PDF
            </button>
          </div>
        </div>
      </div>

      {/* 微信兼容提示：仅屏幕 */}
      <WeChatNotice />

      {/* 报告主体（PDF 打印用这块） */}
      <div ref={reportRef} className="bg-white">
        {/* P1 封面页 */}
        <ReportCover score={score} intent={intent} year={yearParam} />

        {/* P2 你的位置 */}
        <ReportPage_Position result={result} policy={policy} score={score} />

        {/* P3 五校结果 */}
        <ReportPage_Schools result={result} />

        {/* P4 详细指标 */}
        <ReportPage_Metrics result={result} />

        {/* P5–P9 学校介绍 */}
        {PROFILES.schools.map((p) => (
          <ReportPage_SchoolDetail key={p.key} profile={p} probItem={result.schools.find((s) => s.school === p.name)} position={result.position} />
        ))}

        {/* P10 算法说明 */}
        <ReportPage_Algorithm />

        {/* P11 答谢页 */}
        <ReportPage_Footer />
      </div>

      {/* 录取结果回填入口（仅屏幕显示，不进 PDF / 打印） */}
      <div className="print:hidden container-mobile mt-6">
        <BackfillPledge result={result} stageInfo={stageInfo} />
      </div>
    </div>
  )
}

function WeChatNotice() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const inWeChat = /MicroMessenger/i.test(ua)
  if (!inWeChat) return null
  return (
    <div className="print:hidden bg-amber-50 border-b border-amber-100">
      <div className="container-mobile py-2 text-amber-800 text-xs leading-relaxed">
        当前在微信浏览器中查看，"下载 PDF"会在浏览器右上角"…"菜单选择"用其他浏览器打开"以便保存。
        也可直接长按报告截图保存到相册。
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// 报告分页组件（每页 = 一个 section，print 时 page-break-after）
// ───────────────────────────────────────────────────────────────

function ReportSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={'report-page px-6 py-8 ' + className}>
      <div className="container-mobile">{children}</div>
    </section>
  )
}

function ReportCover({ score, intent, year }: { score: number; intent: SchoolName | null; year: number }) {
  return (
    <ReportSection className="bg-gradient-to-b from-brand-100 to-white min-h-[420px] flex flex-col justify-between">
      <div>
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo_1.png" className="h-12 w-12 rounded-xl shadow-soft" alt="方法星球" />
          <div>
            <div className="text-ink-800 font-medium">方法星球</div>
            <div className="text-ink-500 text-xs">卓越教育 · 公益项目</div>
          </div>
        </div>

        <div className="text-ink-500 text-sm mb-2">邹城中考志愿测算 · 个性化报告</div>
        <h1 className="text-[40px] leading-tight text-ink-900 font-medium">
          {score} 分
          <br />
          志愿填报建议
        </h1>

        <div className="mt-6 space-y-1 text-ink-600 text-sm">
          <div>参考年份：{year} 年公告数据</div>
          {intent && <div>家长意向：{intent}</div>}
          <div>生成时间：{new Date().toLocaleDateString('zh-CN')}</div>
        </div>
      </div>

      <div className="mt-12 text-xs text-ink-400 leading-relaxed">
        本报告由 ZSM-1.1 自适应密度修匀模型生成。
        <br />
        基于诺贝尔经济学奖 HP 滤波法 + PCHIP 单调插值 + 蒙特卡洛区间估计。
        <br />
        最终填报请以本年度公告 + 老师建议为准。
      </div>
    </ReportSection>
  )
}

function ReportPage_Position({ result, policy, score }: { result: CalcResult; policy: any; score: number }) {
  const { position } = result
  return (
    <ReportSection>
      <PageHeader index="P 1" title="你的位置" />

      {position.inTop300 ? (
        <div className="card bg-brand-50">
          <div className="text-h2 text-brand-700 mb-2">前 300 名</div>
          <p className="text-ink-700 leading-relaxed">
            按邹城录取规则，前 300 名优先录取至邹城一中。
            分数达到当年前 300 线 {policy.top300_line} 分即享此规则。
          </p>
        </div>
      ) : !position.band ? (
        <div className="card border border-rose-100">
          <div className="text-h2 text-rose-700 mb-2">低于普通生填报志愿资格线</div>
          <p className="text-ink-700">资格线 {policy.qualification_line} 分。请咨询学校了解职普融通等通道。</p>
        </div>
      ) : (
        <>
          <div className="card grid grid-cols-2 gap-4 mb-4">
            <Stat label="所在分数段" value={`${position.band.lower} ≤ 分数 < ${position.band.upper}`} />
            <Stat label="段内人数" value={`${position.band.people} 人`} />
            <Stat label="段内顺位" value={`第 ${position.positionInBand} 顺位`} />
            <Stat label="段内累计位置" value={`${(position.cumulativePosition * 100).toFixed(0)}%`} />
            <div className="col-span-2 pt-3 border-t border-ink-100">
              <div className="text-ink-500 text-sm mb-1">估算全市名次</div>
              <div className="text-h3 text-ink-900 tabular-nums">
                约 {position.estimatedRankRange.min} – {position.estimatedRankRange.max} 名
              </div>
            </div>
          </div>

          <div className="card">
            <div className="text-ink-500 text-sm mb-2">分数分布密度曲线（红线为你的位置）</div>
            <DensityCurve policy={policy} score={score} method="whittaker" />
          </div>
        </>
      )}
    </ReportSection>
  )
}

function ReportPage_Schools({ result }: { result: CalcResult }) {
  return (
    <ReportSection>
      <PageHeader index="P 2" title={result.position.inTop300 ? '录取结果' : '五校录取概率'} />
      <div className="space-y-3">
        {result.schools.map((s) => {
          const st = probStrength(s.point, s.lower, s.upper, s.structuralCertain)
          const showPercent = showProbPercent(s.school, s.tag, s.structuralCertain, result.position)
          const lowerVal = s.structuralCertain
            ? s.lower
            : Math.min(s.lower, s.scenarios.pessimistic, 0.99)
          return (
            <div key={s.school} className="card">
              <div className="flex items-start justify-between">
                <h3 className="text-h3 text-ink-900">{s.school}</h3>
                <span className={'tag ' + tagClass(s.tag)}>{tagLabel(s.tag)}</span>
              </div>
              {s.narrative && (
                <p className="text-ink-600 text-sm mt-1 leading-relaxed">{s.narrative}</p>
              )}
              {showPercent ? (
                <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                  <span className="text-[36px] leading-none font-medium text-ink-900 tabular-nums">
                    {st.display}
                    {st.tone !== 'none' && <span className="text-h3 text-ink-400 ml-0.5">%</span>}
                  </span>
                  <span className={'text-sm font-semibold ' + probToneClass(st.tone)}>{st.label}</span>
                  {st.tone !== 'none' && (
                    <span className="text-ink-500 text-sm tabular-nums">
                      区间 {formatProbPercent(lowerVal)}% –{' '}
                      {formatProbPercent(st.certain ? s.upper : Math.min(s.upper, 0.99))}%
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-3">
                  <span className="text-ink-500 text-sm">录取可能性 </span>
                  <span className={'text-h3 font-semibold ' + probToneClass(st.tone)}>{st.label}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {result.suggestion && (
        <div className="card mt-5 bg-brand-50 border border-brand-100">
          <h3 className="text-h3 text-ink-900 mb-2">填报建议</h3>
          {result.suggestion.first && (
            <div className="text-ink-700 mb-2">
              <span className="text-ink-500 text-sm">第一志愿：</span>
              <span className="text-h3 text-brand-700">{result.suggestion.first}</span>
            </div>
          )}
          {result.suggestion.second && (
            <div className="text-ink-700 mb-2">
              <span className="text-ink-500 text-sm">第二志愿：</span>
              <span className="text-ink-800">{result.suggestion.second}</span>
            </div>
          )}
          <p className="text-ink-700 text-sm leading-relaxed">{result.suggestion.rationale}</p>
          <div className="mt-3 px-3 py-2 bg-amber-50 text-amber-800 text-xs rounded-lg leading-relaxed">
            本建议为模型基于公开规则的辅助参考，最终填报请结合本年度公告、孩子意愿与老师建议综合决定。
          </div>
        </div>
      )}
    </ReportSection>
  )
}

function ReportPage_Metrics({ result }: { result: CalcResult }) {
  return (
    <ReportSection>
      <PageHeader index="P 3" title="6 项专业指标 + 三种情境" />
      <p className="text-ink-600 text-sm mb-5 leading-relaxed">
        三种情境推演：保守 = 假设报该校的人比预测多 20%；中性 = 当前预测；乐观 = 假设报该校的人比预测少 20%。
        若三者跨度大，说明该校受报名习惯波动影响显著。
      </p>

      <div className="space-y-4">
        {result.schools.map((s) => (
          <div key={s.school} className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-h3 text-ink-900">{s.school}</h3>
              <span className={'tag ' + tagClass(s.tag)}>{tagLabel(s.tag)}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <ScenarioCell label="保守" value={s.scenarios.pessimistic} color="bg-rose-50 text-rose-800" />
              <ScenarioCell label="中性" value={s.scenarios.neutral} color="bg-brand-50 text-brand-800" />
              <ScenarioCell label="乐观" value={s.scenarios.optimistic} color="bg-emerald-50 text-emerald-800" />
            </div>

            <div className="grid grid-cols-2 gap-y-2 text-sm pt-3 border-t border-ink-100">
              <MetricLine label="满额预警线" value={`${(s.metrics.burnoutPoint * 100).toFixed(0)}%`} />
              <MetricLine label="报名热度" value={`×${s.metrics.heatPremium.toFixed(2)}`} />
              <MetricLine label="捡漏指数" value={`×${s.metrics.bargainIndex.toFixed(2)}`} />
              <MetricLine label="段内压强" value={`${s.metrics.perScorePressure.toFixed(1)} 人/分`} />
              <MetricLine
                label="顺位安全垫"
                value={`${s.metrics.safetyMargin >= 0 ? '+' : ''}${(s.metrics.safetyMargin * 100).toFixed(0)}%`}
                color={s.metrics.safetyMargin > 0 ? 'text-emerald-700' : s.metrics.safetyMargin < 0 ? 'text-rose-700' : ''}
              />
            </div>
          </div>
        ))}
      </div>
    </ReportSection>
  )
}

function ReportPage_SchoolDetail({
  profile,
  probItem,
  position
}: {
  profile: typeof PROFILES.schools[number]
  probItem?: CalcResult['schools'][number]
  position: CalcResult['position']
}) {
  return (
    <ReportSection>
      <PageHeader index={`P ${4 + (PROFILES.schools.indexOf(profile))}`} title={profile.name + ' · 学校介绍'} />
      {probItem && (() => {
        const st = probStrength(probItem.point, probItem.lower, probItem.upper, probItem.structuralCertain)
        const showPercent = showProbPercent(probItem.school, probItem.tag, probItem.structuralCertain, position)
        const lowerVal = probItem.structuralCertain
          ? probItem.lower
          : Math.min(probItem.lower, probItem.scenarios.pessimistic, 0.99)
        return (
          <div className="card bg-brand-50/60 mb-4 flex items-baseline justify-between">
            <div>
              <div className="text-ink-500 text-sm">{showPercent ? '你的录取概率' : '你的录取可能性'}</div>
              <div className="text-h2 text-brand-700 tabular-nums">
                {showPercent ? (
                  <>
                    {st.display}
                    {st.tone !== 'none' && '%'}{' '}
                    <span className={'text-base font-semibold ' + probToneClass(st.tone)}>{st.label}</span>
                    {st.tone !== 'none' && (
                      <span className="text-ink-500 text-base ml-1">
                        ({formatProbPercent(lowerVal)}%–
                        {formatProbPercent(st.certain ? probItem.upper : Math.min(probItem.upper, 0.99))}%)
                      </span>
                    )}
                  </>
                ) : (
                  <span className={'font-semibold ' + probToneClass(st.tone)}>{st.label}</span>
                )}
              </div>
            </div>
            <span className={'tag ' + tagClass(probItem.tag)}>{tagLabel(probItem.tag)}</span>
          </div>
        )
      })()}
      <SchoolProfileCard profile={profile} />
    </ReportSection>
  )
}

function ReportPage_Algorithm() {
  return (
    <ReportSection>
      <PageHeader index="P 9" title="ZSM-1.1 模型说明" />
      <div className="card space-y-4 text-ink-700 leading-relaxed">
        <p>
          <span className="text-ink-900 font-medium">模型定位：</span>
          基于邹城市教育和体育局历年公开的 10 分段表与录取规则，对单个考生在五所高中的录取概率给出
          <span className="text-brand-700">区间估计 + 多情境推演</span>。
        </p>
        <p>
          <span className="text-ink-900 font-medium">密度重建：</span>
          采用 1924 年由 R. Henderson 提出、后被 Hodrick & Prescott 在宏观经济学中改造的
          <span className="text-brand-700"> Whittaker-Henderson 修匀 / HP 滤波 </span>
          方法（Edward Prescott 凭 HP 滤波框架获 2004 年诺贝尔经济学奖），
          从粗 10 分段表恢复细密度，并用 PCHIP 单调插值作为兜底。
        </p>
        <p>
          <span className="text-ink-900 font-medium">区间估计：</span>
          对偏好矩阵做 200 次蒙特卡洛抖动（±10%），取 20–80 百分位作为概率区间。
        </p>
        <p>
          <span className="text-ink-900 font-medium">模型校准：</span>
          每届考试后向考生家长收集真实录取结果，对偏好矩阵进行最大似然校准；
          模型版本号每年递增（ZSM-1.1 → ZSM-1.2 → …）。
        </p>
        <div className="px-3 py-2 bg-amber-50 text-amber-800 text-xs rounded-lg leading-relaxed">
          模型局限：依赖历史数据推断当年报名习惯；如遇政策变化或舆情扰动，预测准确度会下降。
          建议作为辅助参考，不能替代老师面谈。
        </div>
      </div>
    </ReportSection>
  )
}

function ReportPage_Footer() {
  return (
    <ReportSection className="bg-gradient-to-b from-white to-brand-50">
      <PageHeader index="P 10" title="关于方法星球" />
      <div className="card text-ink-700 leading-relaxed">
        <p>
          方法星球，由
          <span className="text-ink-900 font-medium">邹城市卓越教育科技有限公司</span>
          倾力打造的公益服务项目，致力于用更科学的方法帮助本地家庭做出关键学业决策。
        </p>
        <p className="mt-3">
          本报告完全免费。如本工具对你有帮助，欢迎关注我们的微信服务号
          <span className="text-brand-700">「卓越方法星球」</span>
          ，获取后续政策解读和高中三年学业指导。
        </p>
        <div className="mt-5 flex flex-col items-center gap-3 py-4 border-t border-ink-100">
          <div className="w-32 h-32 bg-white border border-ink-200 rounded-lg overflow-hidden">
            <img
              src="/oa-qr.png"
              alt="关注公众号 - 卓越方法星球"
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="text-ink-500 text-sm">长按识别 · 关注方法星球</div>
        </div>
      </div>

      <div className="card mt-4 text-xs text-ink-400 leading-relaxed">
        <div className="text-ink-700 text-sm mb-2">免责声明</div>
        本报告由算法基于历史公开数据生成，不构成填报承诺，不替代官方公告与教师面谈建议。
        卓越教育科技有限公司对因使用本报告做出的任何决策不承担责任。
        模型预测存在误差，最终录取以邹城市教育和体育局公告为准。
      </div>
    </ReportSection>
  )
}

// ───────────────────────────────────────────────────────────────
// 小组件
// ───────────────────────────────────────────────────────────────
function PageHeader({ index, title }: { index: string; title: string }) {
  return (
    <div className="mb-5">
      <div className="text-ink-400 text-xs tracking-widest">{index}</div>
      <h2 className="text-h2 text-ink-900 mt-1">{title}</h2>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-ink-500 text-sm">{label}</div>
      <div className="text-h3 text-ink-900 tabular-nums">{value}</div>
    </div>
  )
}

function ScenarioCell({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className={'rounded-xl px-3 py-2 text-center ' + color}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-h3 tabular-nums">{formatProbPercent(value)}%</div>
    </div>
  )
}

function MetricLine({ label, value, color = '' }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div className="text-ink-500 text-xs">{label}</div>
      <div className={'tabular-nums ' + (color || 'text-ink-900')}>{value}</div>
    </div>
  )
}

function tagLabel(t: string): string {
  return ({ safe: '稳', recommended: '可报', reach: '冲', risky: '风险', unlikely: '低概率' } as any)[t] ?? t
}
function tagClass(t: string): string {
  return ({ safe: 'tag-safe', recommended: 'tag-recommended', reach: 'tag-reach', risky: 'tag-risky', unlikely: 'tag-unlikely' } as any)[t] ?? ''
}
